import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { Component, inject, OnInit, Input, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { SITE_TYPE_OPTIONS, SiteList, normalizeSiteType } from 'src/app/site-list/model/site-list.model';
import { UsersService } from 'src/app/services/users.service';
import { UtilService } from 'src/app/services/util.service';
import { SiteService, TemporarySchedulePayload } from 'src/app/services/site.service';
import { DwService } from 'src/app/services/dw.service';
import { TeamHierarchyService } from 'src/app/services/team-hierarchy.service';
import { AdminService } from 'src/app/services/admin.service';
import { userRoleNameEnum } from 'src/app/shared/const/enum';

import { BsModalService } from 'ngx-bootstrap/modal';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, forkJoin, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SwSelectComponent } from 'src/app/shared/components/sw-select/sw-select.component';
import { DatePickerModule } from 'primeng/datepicker';
import { SwDrawerComponent } from 'src/app/shared/components/sw-drawer/sw-drawer.component';
import { ListOfScheduleComponent } from 'src/app/users/list-of-schedule/list-of-schedule.component';
import { ScheduleService } from 'src/app/services/schedule.service';
import { RtspCameraService } from 'src/app/services/rtsp-camera.service';
import { firstValueFrom } from 'rxjs';
import { PbxSpeakerSuggestComponent } from '../pbx-speaker-suggest/pbx-speaker-suggest.component';
import { PbxSpeakerPick } from '../../models/pbx.model';
import { AlertService } from 'src/app/services/alert.service';
import { ResponseProtocolService } from 'src/app/services/response-protocol.service';
import { ResponseOrderEditorComponent } from '../response-order-editor/response-order-editor.component';
import { editableResponseSteps, resolveResponseSteps } from '../../const/response-protocol';
import { ResponseStep } from '../../models/response-protocol.model';

/** One visitor window. No `scope`/`cameras`: at add time every window covers the whole site. */
interface VisitorScheduleRow {
  start: Date | null;
  end: Date | null;
  note: string;
  /** Only ever 'all' at add time; kept so the picker has something to bind to. */
  scope: 'all';
}

@Component({
  selector: 'app-client-modal',
  templateUrl: './site-modal.component.html',
  styleUrls: ['./site-modal.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, SwSelectComponent, NgbTooltip, DatePickerModule, SwDrawerComponent,
    ListOfScheduleComponent, PbxSpeakerSuggestComponent, ResponseOrderEditorComponent, NgTemplateOutlet]
})
export class SiteModalComponent implements OnInit {

  // Rendered inside the shared <sw-drawer> (app.component). When embedded the
  // panel hides its own header and closes via SiteService instead of the modal.
  @Input() embedded: boolean = false;

  private fb = inject(UntypedFormBuilder);
  private modalService = inject(BsModalService);
  private _siteService = inject(SiteService);
  private _utils = inject(UtilService);
  private _userService = inject(UsersService);
  private _dwService = inject(DwService);
  private _teamService = inject(TeamHierarchyService);
  private _adminService = inject(AdminService);
  private _scheduleService = inject(ScheduleService);
  private _rtspCameraService = inject(RtspCameraService);
  private _alertService = inject(AlertService);
  private _responseProtocol = inject(ResponseProtocolService);

  // Incident response order. With the default on, the site follows Settings live and its own
  // steps are not saved; turned off, the site may only reorder the steps Settings defines.
  responseUseDefault = true;
  responseSteps: ResponseStep[] = [];
  defaultResponseSteps: ResponseStep[] = [];
  private savedSiteResponseSteps: ResponseStep[] = [];
  private responseDefaultSubscription?: Subscription;
  // PBX speakers added with a camera; saved as Speaker records once the site save succeeds.
  private pbxSpeakerPicks: PbxSpeakerPick[] = [];
  // The PBX speaker list needs the full form width for its card grid.
  pbxPanelOpen = false;

  updateSubscription: Subscription;
  dwLayoutsSubscription: Subscription;
  dwServersSubscription: Subscription;
  dwUsersSubscription: Subscription;
  dwDeviceTypesSubscription: Subscription;
  dwEventsSubscription: Subscription;
  dwActionsSubscription: Subscription;
  siteDetailSubscription: Subscription;
  addSiteForm!: UntypedFormGroup;
  isLoading: boolean = false;
  siteData: SiteList;
  dwUsers: Array<any> = [];
  isDwUsersLoading: boolean = false;
  deviceTypes: Array<any> = [];
  isDeviceTypesLoading: boolean = false;
  dwEvents: Array<{ label: string; value: string; raw?: any }> = [];
  isDwEventsLoading: boolean = false;
  dwActions: Array<{ label: string; value: string; raw?: any }> = [];
  isDwActionsLoading: boolean = false;
  deviceSequence: number = 1;
  isEditMode: boolean = false;
  dwLayouts: Array<{ label: string; value: string; id: string; name: string; raw?: any }> = [];
  isDwLayoutsLoading: boolean = false;
  dwServers: Array<{ label: string; value: string; id: string; name: string; raw?: any }> = [];
  isDwServersLoading: boolean = false;
  // Teams this site is assigned to. Persisted by updating each team's sitesId
  // (site create/update API has no team field).
  allTeams: Array<any> = [];
  isTeamsLoading: boolean = false;
  readonly siteTypeOptions = SITE_TYPE_OPTIONS;
  /**
   * What Assign Team decides beyond assignment, shown in the help tooltip beside its label.
   * Same shape as the sites list's `helpTips` entries so both render through one card.
   */
  readonly teamHelpTip = {
    title: 'Assign Team',
    body: 'Every manager on the assigned teams receives an email when a critical incident is reported at this site.',
    pointsTitle: 'Incident report answers that trigger the email:',
    points: [
      'Police contacted',
      'Items / material stolen',
      'Suspects',
    ],
  };

  teamsSubscription: Subscription;
  private pendingDwEventIds: string[] | null = null;
  private pendingDwActionIds: string[] | null = null;
  private pendingLayoutId: string | null = null;
  private pendingLayoutName: string | null = null;
  private pendingDwUserIds: string[] | null = null;
  private pendingDwUserRefs: any[] | null = null;
  private siteDataInitialized = false;
  private dwUsersLoaded = false;

  constructor() {
    this.addSiteForm = this.fb.group({
      _id: [''],
      site_name: ['', [Validators.required]],
      display_name: ['', [Validators.required]],
      CRM_url: ['', [Validators.required]],
      // A number to reach the client on for this site. Optional, and nothing keys off it.
      client_contact_number: [''],
      // Construction / residential / commercial. Optional: null leaves the site unclassified.
      // A construction site's alerts say that monitoring has started and no workers are on site.
      site_type: [null],
      // Call numbers stored on the site: speaker talk-down (a site can have
      // several speakers) and emergency dispatch.
      speaker_numbers: this.fb.array([this.fb.control('')]),
      emergency_call_number: [''],
      DW_server: [null],
      layout_option: ['new'],
      layout_name: [''],
      existing_layout: [null],
      DW_user: [null],
      DW_events: [[]],
      DW_actions: [[]],
      // Persisted on the team side (team_hierarchy.sitesId), not in the site payload — see
      // syncSiteTeams. It is also what the critical-incident email reads to find its
      // recipients, so a site with no team assigned notifies nobody.
      assigned_teams: [[]],
      // Site option toggles (managed at add time; sent in the create payload).
      is_schedule_active: [true],
      is_alert_active: [true],
      is_alert_email_send_active: [false],
      receive_alert_send_email: [''],
      is_archive_alert_active: [false],
      is_face_detection: [true],
      is_vehicle_detection: [true],
      is_zone_detection: [false],
      is_rtsp_recording: [false],
      DW_devices: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this._responseProtocol.ensureLoaded();
    this.responseDefaultSubscription = this._responseProtocol.defaultSteps$.subscribe((steps) => {
      this.defaultResponseSteps = editableResponseSteps(steps);
      this.syncSiteResponseSteps();
    });
    this.siteData = this._userService.siteData;
    this.isEditMode = !!this.siteData;
    this.pendingDwEventIds = null;
    this.pendingDwActionIds = null;
    this.pendingLayoutId = null;
    this.pendingLayoutName = null;
    this.pendingDwUserIds = null;
    this.pendingDwUserRefs = null;
    this.siteDataInitialized = false;
    this.dwUsersLoaded = false;

    if (this.isEditMode && this.siteData?._id) {
      this.loadSiteDetailAndInit();
      return;
    }

    if (this.siteData) {
      this.initFromSiteData(this.siteData);
    }

    this.loadDependentData();
  }

  private loadSiteDetailAndInit(): void {
    this.siteDetailSubscription = this._userService.getMasterUserId().pipe(
      switchMap((userId) => {
        if (!userId) {
          return throwError(() => new Error('master user unavailable'));
        }
        return this._siteService.getSiteDetail(this.siteData._id, userId);
      })
    ).subscribe({
      next: (res: any) => {
        const detail = res?.site ?? res?.data ?? res ?? {};
        this.siteData = { ...this.siteData, ...detail };
        this.initFromSiteData(this.siteData);
        // Live urls come off the same detail response Site Settings reads them from.
        this.setLiveUrlRows(detail);
        this.loadDependentData();
      },
      error: () => {
        this.initFromSiteData(this.siteData);
        this.loadDependentData();
      },
      complete: () => {
        if (this.siteDetailSubscription) {
          this.siteDetailSubscription.unsubscribe();
        }
      },
    });
  }

  private loadDependentData(): void {
    this.loadDwUsers();
    this.loadDeviceTypes();
    this.loadDwEvents();
    this.loadDwActions();
    this.loadDwLayouts();
    this.loadDwServers();
    this.loadTeams();
    this.setupLayoutOptionListener();
    this.setupSiteNameListener();
    this.setupDwUserListener();

    if (!this.isEditMode && this.devices.length === 0) {
      this.addDevice();
    }
  }

  loadTeams(): void {
    this.isTeamsLoading = true;
    this.teamsSubscription = this._teamService
      .listTeamHierarchy({ pagination: { page: 1, perPage: 1000000 }, sort: { created_at: -1 }, query: {} })
      .subscribe({
        next: (res: any) => {
          const rawList = Array.isArray(res) ? res : (res?.list ?? res?.data ?? []);
          this.allTeams = (Array.isArray(rawList) ? rawList : []).map((t: any) => ({
            ...t,
            _id: t?._id ?? t?.id,
            team_name: t?.team_name ?? t?.name ?? `${t?._id ?? ''}`,
          }));

          // Edit mode: preselect teams that already contain this site.
          const siteId = this.siteData?._id ? `${this.siteData._id}` : null;
          if (this.isEditMode && siteId) {
            const preselected = this.allTeams
              .filter((t) => Array.isArray(t.sitesId) && t.sitesId.map((id: any) => `${id}`).includes(siteId))
              .map((t) => t._id);
            this.addSiteForm.patchValue({ assigned_teams: preselected }, { emitEvent: false });
          }
          this.isTeamsLoading = false;
        },
        error: (e) => { this.isTeamsLoading = false; console.log(e); },
        complete: () => { if (this.teamsSubscription) { this.teamsSubscription.unsubscribe(); } },
      });
  }

  // Assignment lives on the team side (team.sitesId), so add/remove this site
  // from each team's sitesId via team_hierarchy add_update. Only changed teams
  // are updated; each payload preserves the team's existing fields.
  /**
   * Authorised Visitor Alert Schedule, collected while the site is being added.
   *
   * Site Settings offers this per camera as well, but a camera only exists once its first
   * alert has arrived — `Camera.create` runs on the alert path, not when a site is saved. So
   * at add time there is nothing to pick from and every window here covers the whole site,
   * which is what the picker defaults to anyway. Camera-specific windows stay where they
   * already are, in Site Settings, once the cameras are there to choose.
   *
   * Rows are held here and written after the site exists, because the endpoint is keyed by
   * site id. The schedule feature itself is untouched — this is the same payload the
   * settings drawer sends, to the same endpoint.
   */
  visitorScheduleDrawerOpen = false;
  visitorScheduleSubmitted = false;
  visitorScheduleNow: Date = new Date();
  visitorScheduleToday: Date = new Date();
  visitorScheduleRows: VisitorScheduleRow[] = [];
  /**
   * The sites list lists every camera here. This site has none — a camera is created by its
   * first alert — so the whole site is the only thing a window can apply to yet.
   */
  readonly visitorScheduleScopeOptions = [{ label: 'All cameras', value: 'all' }];
  /** What the rows looked like when the drawer opened, so Discard can put them back. */
  private visitorScheduleBaseline = '[]';

  /**
   * Alert Schedule, opened once the site exists.
   *
   * `app-list-of-schedule` reads and writes against a site id it is handed — it lists the
   * site's schedules on open and saves each row as it is added. There is no id to give it
   * until the site is created, and giving that component a draft mode would change how the
   * schedule feature behaves everywhere else it is used.
   *
   * So the row is offered here, and the same drawer opens the moment Submit has created the
   * site. The add form stays open behind it and closes when the drawer does, which keeps
   * adding a site and setting its schedule one continuous action.
   */
  @ViewChild(ListOfScheduleComponent) private scheduleEditor?: ListOfScheduleComponent;
  alertScheduleDrawerOpen = false;
  /** Set once Submit has been pressed, so the "required" error only shows after a real attempt. */
  alertScheduleSubmitted = false;
  /** Schedules drafted while adding, waiting for a site id. */
  alertScheduleDrafts: any[] = [];
  /** What was drafted before the drawer opened, so Discard can go back to it. */
  private alertScheduleBaseline: any[] = [];
  schedSiteId = '';
  schedUserId = '';
  schedId: string | null = null;
  schedDateDaysActive = false;
  /**
   * Opens the schedule editor. On an existing site it reads and writes as it always has; on
   * a new one it runs in draft mode and the rows are sent once Submit has made a site to send
   * them for.
   */
  requestAlertSchedule(): void {
    this.schedSiteId = this.isEditMode ? `${this.siteData?._id ?? ''}` : '';
    // A site being added has no monitoring record yet, so there is nothing to carry in.
    this.schedId = null;
    this.schedDateDaysActive = false;
    this._utils.setLocalStorage('is_date_days_active', false);
    this.alertScheduleBaseline = [...this.alertScheduleDrafts];
    this._userService.getMasterUserId().subscribe((userId: string) => {
      this.schedUserId = userId;
      this.alertScheduleDrawerOpen = true;
    });
  }

  /**
   * Keeps what was drafted and closes. The rows are sent once Submit has created a site to
   * send them for; nothing has been written yet.
   */
  saveAlertScheduleRows(): void {
    this.alertScheduleDrafts = this.scheduleEditor?.getDraftSchedules() ?? this.alertScheduleDrafts;
    this.alertScheduleDrawerOpen = false;
  }

  /** Throws away everything drafted since the drawer was opened. */
  discardAlertScheduleRows(): void {
    this.alertScheduleDrafts = [...this.alertScheduleBaseline];
    this.alertScheduleDrawerOpen = false;
  }

  /**
   * Closing any other way — the back arrow, the mask — keeps what was there before the drawer
   * opened. Only Save takes the drafts; anything else leaves them untouched.
   */
  closeAlertScheduleDrawer(): void {
    this.alertScheduleDrawerOpen = false;
  }

  get alertScheduleCount(): number {
    return this.alertScheduleDrafts.length;
  }


  private saveAlertScheduleDrafts(siteId: string): void {
    if (!siteId || !this.alertScheduleDrafts.length) {
      return;
    }
    const requests = this.alertScheduleDrafts.map((params) =>
      this._scheduleService.addSchedule({ ...params, site: siteId }, 2)
    );
    forkJoin(requests).subscribe({
      error: () => this._utils.showErrorInfo(
        '',
        'Site was created, but the alert schedule could not be saved. Please add it from Site Settings.'
      ),
    });
  }

  openVisitorScheduleDrawer(): void {
    if (!this.visitorScheduleRows.length) {
      this.addVisitorScheduleRow();
    }
    this.visitorScheduleNow = SiteModalComponent.newYorkNow();
    // Midnight, not the current hour: the floor is the day, so the whole of today stays
    // selectable. Leaving the time on it made every hour before now unclickable.
    this.visitorScheduleToday = new Date(this.visitorScheduleNow);
    this.visitorScheduleToday.setHours(0, 0, 0, 0);
    this.visitorScheduleBaseline = JSON.stringify(this.visitorScheduleRows);
    this.visitorScheduleSubmitted = false;
    this.visitorScheduleDrawerOpen = true;
  }

  /**
   * Closes the drawer with the rows kept. Nothing is written yet — the endpoint is keyed by
   * site id, so these go up the moment Submit creates the site.
   */
  saveVisitorScheduleRows(): void {
    this.visitorScheduleSubmitted = true;
    if (this.visitorScheduleInvalid) {
      return;
    }
    this.visitorScheduleDrawerOpen = false;
  }

  /** Puts the rows back to what they were when the drawer opened. */
  discardVisitorScheduleRows(): void {
    const restored = JSON.parse(this.visitorScheduleBaseline) as VisitorScheduleRow[];
    // JSON turns the dates into strings on the way through; the pickers need Date objects.
    this.visitorScheduleRows = restored.map((row) => ({
      start: row.start ? new Date(row.start) : null,
      end: row.end ? new Date(row.end) : null,
      note: row.note ?? '',
      scope: row.scope ?? 'all',
    }));
    if (!this.visitorScheduleRows.length) {
      this.addVisitorScheduleRow();
    }
    this.visitorScheduleSubmitted = false;
    this.visitorScheduleDrawerOpen = false;
  }

  addVisitorScheduleRow(): void {
    this.visitorScheduleRows.unshift({ start: null, end: null, note: '', scope: 'all' });
  }

  removeVisitorScheduleRow(index: number): void {
    this.visitorScheduleRows.splice(index, 1);
    if (!this.visitorScheduleRows.length) {
      this.addVisitorScheduleRow();
    }
  }

  clearVisitorScheduleRows(): void {
    this.visitorScheduleRows = [{ start: null, end: null, note: '', scope: 'all' }];
    this.visitorScheduleSubmitted = false;
  }

  /** A row the user started but left empty carries nothing and is simply dropped. */
  visitorScheduleRowBlank(row: VisitorScheduleRow): boolean {
    return !row.start && !row.end && !row.note.trim();
  }

  /** Nothing entered anywhere, so there is nothing for Clear All to clear. */
  get visitorScheduleEmpty(): boolean {
    return this.visitorScheduleRows.every((row) => this.visitorScheduleRowBlank(row));
  }

  // ── Date helpers and validation, matching the sites list editor field for field ─────────
  visitorScheduleEndDefaultDate(row: VisitorScheduleRow): Date {
    return row.start || this.visitorScheduleNow;
  }

  visitorScheduleEndMinDate(row: VisitorScheduleRow): Date {
    return row.start || this.visitorScheduleToday;
  }

  onVisitorScheduleStartChange(row: VisitorScheduleRow): void {
    if (row.start && row.end && row.end.getTime() <= row.start.getTime()) {
      row.end = null;
    }
  }

  visitorScheduleSpanInvalid(row: VisitorScheduleRow): boolean {
    return !!row.start && !!row.end && row.end.getTime() <= row.start.getTime();
  }

  visitorScheduleWindowLabel(row: VisitorScheduleRow): string {
    if (!row.start || !row.end || this.visitorScheduleSpanInvalid(row)) {
      return '';
    }
    const stamp = (value: Date) => value.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
    const covered = new Date(row.start.getTime() - 30 * 60000);
    const until = new Date(row.end.getTime() + 30 * 60000);
    return `${stamp(covered)} – ${stamp(until)}, 30 min added either side`;
  }

  private visitorScheduleShowErrors(row: VisitorScheduleRow): boolean {
    return this.visitorScheduleSubmitted && !this.visitorScheduleRowBlank(row);
  }

  visitorScheduleStartError(row: VisitorScheduleRow): string {
    if (!this.visitorScheduleShowErrors(row)) {
      return '';
    }
    return row.start ? '' : 'Start time is required.';
  }

  visitorScheduleEndError(row: VisitorScheduleRow): string {
    if (this.visitorScheduleSpanInvalid(row)) {
      return 'End must be after start.';
    }
    if (!this.visitorScheduleShowErrors(row)) {
      return '';
    }
    return row.end ? '' : 'End time is required.';
  }

  visitorScheduleNoteError(row: VisitorScheduleRow): string {
    if (!this.visitorScheduleShowErrors(row)) {
      return '';
    }
    return row.note.trim() ? '' : 'Note is required.';
  }

  private visitorScheduleRowInvalid(row: VisitorScheduleRow): boolean {
    if (this.visitorScheduleRowBlank(row)) {
      return false;
    }
    return !row.start || !row.end || this.visitorScheduleSpanInvalid(row) || !row.note.trim();
  }

  /**
   * Checked before the site is submitted, so a half-filled window is caught here rather than
   * being silently dropped after the site has already been created.
   */
  get visitorScheduleInvalid(): boolean {
    return this.visitorScheduleRows.some((row) => this.visitorScheduleRowInvalid(row));
  }

  get visitorScheduleCount(): number {
    return this.visitorScheduleRows.filter((row) => !this.visitorScheduleRowBlank(row)).length;
  }

  /**
   * Now, as New York reads it — the sites list builds the same value for the same pickers.
   * Schedules are written and matched in that wall clock, so a browser in another zone must
   * not be offered its own idea of "today".
   */
  private static newYorkNow(): Date {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());
    const at = (type: string) => +(parts.find((part) => part.type === type)?.value ?? 0);
    return new Date(at('year'), at('month') - 1, at('day'), at('hour') % 24, at('minute'), 0, 0);
  }

  /** Same wall-clock format the settings drawer sends, so the two write identical records. */
  private static dateToWallClock(value: Date | null): string | null {
    if (!(value instanceof Date) || isNaN(value.getTime())) {
      return null;
    }
    const p = (n: number) => `${n}`.padStart(2, '0');
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`
      + ` ${p(value.getHours())}:${p(value.getMinutes())}`;
  }

  private visitorSchedulePayload(): TemporarySchedulePayload[] {
    return this.visitorScheduleRows
      .filter((row) => !this.visitorScheduleRowBlank(row))
      .map((row) => ({
        id: null,
        // Empty means the whole site, which is the only scope available at add time.
        cameras: [],
        starts_at: SiteModalComponent.dateToWallClock(row.start),
        ends_at: SiteModalComponent.dateToWallClock(row.end),
        note: row.note.trim(),
      }));
  }

  /**
   * Written after the site exists, alongside the team sync. Deliberately not fatal: the site
   * has already been created by this point, so a failure here is reported and the schedule
   * can be added from Site Settings rather than the whole save appearing to have failed.
   */
  private saveVisitorSchedules(siteId: string): void {
    const schedules = this.visitorSchedulePayload();
    if (!siteId || !schedules.length) {
      return;
    }
    this._siteService.saveSiteTemporarySchedules(siteId, schedules).subscribe({
      error: () => this._utils.showErrorInfo(
        '',
        'Site was created, but the visitor schedule could not be saved. Please add it from Site Settings.'
      ),
    });
  }


  /**
   * Live URLs — the RTSP streams the dashboard's live view plays for this site.
   *
   * Lifted from Site Settings so an existing site can be given its streams from the same form
   * that edits everything else about it. Same rows, same endpoint, same rule that a url must
   * be rtsp://; only the screen it is shown on is new.
   *
   * Edit only. A site being added has no id to save them against, and its devices are still
   * being created in the form above.
   */
  liveUrlRows: { id: string | null; name: string; url: string; username: string; password: string; saved: boolean; reveal: boolean }[] = [];
  private liveUrlsBaseline = '';

  private static isRtsp(value: any): boolean {
    return typeof value === 'string' && /^rtsp:\/\//i.test(value.trim());
  }

  /** The first rtsp:// address a device carries, wherever it happens to be recorded. */
  private static deviceRtspUrl(device: any): string {
    const direct = [device?.DW_ip_address, device?.url, device?.streamUrl, device?.rtsp_url]
      .map((value: any) => `${value ?? ''}`.trim())
      .find((value: string) => SiteModalComponent.isRtsp(value));
    if (direct) {
      return direct;
    }
    const streamUrls = device?.streamUrls;
    if (streamUrls && typeof streamUrls === 'object') {
      const fromStreams = Object.values(streamUrls)
        .map((value: any) => `${value ?? ''}`.trim())
        .find((value: string) => SiteModalComponent.isRtsp(value));
      if (fromStreams) {
        return fromStreams;
      }
    }
    return '';
  }

  /**
   * Saved urls if the site has any; otherwise the ones its devices already carry, offered as
   * a starting point rather than making the operator retype what is on the device.
   */
  private setLiveUrlRows(detail: any): void {
    const saved = Array.isArray(detail?.live_urls) ? detail.live_urls : [];
    if (saved.length) {
      this.applyLiveUrlResponse(saved);
      return;
    }
    const devices = Array.isArray(detail?.DW_devices) ? detail.DW_devices : [];
    this.liveUrlRows = devices
      .map((device: any) => ({ device, url: SiteModalComponent.deviceRtspUrl(device) }))
      .filter((entry: any) => !!entry.url)
      .map((entry: any) => ({
        id: null,
        name: `${entry.device?.name ?? ''}`.trim(),
        url: entry.url,
        username: `${entry.device?.DW_username ?? entry.device?.credentials?.user ?? ''}`.trim(),
        password: `${entry.device?.DW_password ?? entry.device?.credentials?.password ?? ''}`.trim(),
        saved: false,
        reveal: false,
      }));
    this.liveUrlsBaseline = this.liveUrlsSnapshot();
  }

  private applyLiveUrlResponse(liveUrls: any[]): void {
    this.liveUrlRows = (Array.isArray(liveUrls) ? liveUrls : []).map((row: any) => ({
      id: `${row?.id ?? ''}`.trim() || null,
      name: `${row?.name ?? ''}`,
      url: `${row?.url ?? ''}`,
      username: `${row?.username ?? ''}`,
      password: `${row?.password ?? ''}`,
      saved: true,
      reveal: false,
    }));
    this.liveUrlsBaseline = this.liveUrlsSnapshot();
  }

  private liveUrlsSnapshot(): string {
    return JSON.stringify(
      this.liveUrlRows.map((row) => [row.id, row.name, row.url.trim(), row.username, row.password])
    );
  }

  get liveUrlsDirty(): boolean {
    return this.liveUrlsSnapshot() !== this.liveUrlsBaseline;
  }

  addLiveUrlRow(): void {
    this.liveUrlRows = [
      { id: null, name: '', url: '', username: '', password: '', saved: false, reveal: false },
      ...this.liveUrlRows,
    ];
  }

  removeLiveUrlRow(index: number): void {
    const row = this.liveUrlRows[index];
    if (!row) {
      return;
    }
    const label = row.name?.trim() || row.url?.trim() || `Camera ${index + 1}`;
    this._utils.confirmationAlert(
      'Remove live url',
      `Remove "${label}" from this site's live view?`,
      (confirmed: any) => {
        if (!confirmed) {
          return;
        }
        this.liveUrlRows = this.liveUrlRows.filter((entry) => entry !== row);
      }
    );
  }

  toggleLiveUrlReveal(index: number): void {
    const row = this.liveUrlRows[index];
    if (row) {
      row.reveal = !row.reveal;
    }
  }

  private liveUrlsPayload(): { id: string | null; name: string; url: string; username: string; password: string }[] {
    return this.liveUrlRows
      .map((row) => ({
        id: row.id,
        name: row.name,
        url: row.url.trim(),
        username: row.username,
        password: row.password,
      }))
      .filter((row) => !!row.url);
  }

  /** Written on Save, and only when something about them changed. */
  private persistLiveUrls(siteId: string): Promise<void> {
    if (!siteId || !this.liveUrlsDirty) {
      return Promise.resolve();
    }
    return firstValueFrom(this._siteService.saveSiteLiveUrls(siteId, this.liveUrlsPayload()))
      .then((res: any) => {
        this.applyLiveUrlResponse(res?.live_urls ?? []);
        this._rtspCameraService.clearCache();
      });
  }

  private syncSiteTeams(siteId: string, selectedTeamIds: string[], siteName: string): void {
    if (!siteId || !this.allTeams.length) {
      return;
    }
    const selected = new Set((selectedTeamIds ?? []).map((id) => `${id}`));
    const sid = `${siteId}`;
    const requests: any[] = [];

    for (const team of this.allTeams) {
      const teamId = team?._id;
      if (!teamId) {
        continue;
      }
      const currentIds = (Array.isArray(team.sitesId) ? team.sitesId : []).map((id: any) => `${id}`);
      const currentNames = Array.isArray(team.siteNames) ? [...team.siteNames] : [];
      const has = currentIds.includes(sid);
      const should = selected.has(`${teamId}`);
      if (has === should) {
        continue;
      }

      let sitesId: string[];
      let siteNames: string[];
      if (should && !has) {
        sitesId = [...currentIds, sid];
        siteNames = siteName && !currentNames.includes(siteName) ? [...currentNames, siteName] : currentNames;
      } else {
        sitesId = currentIds.filter((id) => id !== sid);
        siteNames = siteName ? currentNames.filter((n) => n !== siteName) : currentNames;
      }

      requests.push(this._teamService.addTeamHierarchy({
        team_name: team.team_name,
        description: team.description,
        sitesId,
        siteNames,
        shifts: team.shifts ?? [],
        DW_user_list: team.DW_user_list ?? [],
        type: 1,
        _id: teamId,
      }));
    }

    if (requests.length) {
      forkJoin(requests).subscribe({
        next: () => { },
        error: (e) => console.log(e),
      });
    }
  }

  private getRawDwUsers(siteData: any): any[] {
    if (Array.isArray(siteData?.DW_user)) {
      return siteData.DW_user;
    }
    if (Array.isArray(siteData?.dw_user)) {
      return siteData.dw_user;
    }
    if (siteData?.DW_user) {
      return [siteData.DW_user];
    }
    if (siteData?.dw_user) {
      return [siteData.dw_user];
    }
    if (Array.isArray(siteData?.dwUser)) {
      return siteData.dwUser;
    }
    if (siteData?.dwUser) {
      return [siteData.dwUser];
    }
    return [];
  }

  private collectSavedUserIds(siteData: any, rawDwUsers: any[]): string[] {
    const collectedUserIds = new Set<string>();

    if (Array.isArray(siteData?.userIds)) {
      siteData.userIds.forEach((id: any) => {
        const trimmed = `${id ?? ''}`.trim();
        if (trimmed) {
          collectedUserIds.add(trimmed);
        }
      });
    }

    rawDwUsers.forEach((user: any) => {
      const normalized = this.normalizeDwUserOption(user);
      if (!normalized) {
        return;
      }
      const value = `${normalized.value ?? ''}`.trim();
      const id = `${normalized.id ?? ''}`.trim();
      if (value) {
        collectedUserIds.add(value);
      }
      if (id) {
        collectedUserIds.add(id);
      }
    });

    const devices = siteData?.DW_devices ?? siteData?.devices ?? [];
    if (Array.isArray(devices)) {
      devices.forEach((device: any) => {
        const engineIds = device?.userEnabledAnalyticsEngineIds ?? [];
        if (!Array.isArray(engineIds)) {
          return;
        }
        engineIds.forEach((id: any) => {
          const trimmed = `${id ?? ''}`.trim();
          if (trimmed) {
            collectedUserIds.add(trimmed);
          }
        });
      });
    }

    return Array.from(collectedUserIds);
  }

  private initFromSiteData(currentSiteData: any): void {
    const rawDwUsers = this.getRawDwUsers(currentSiteData);
    const normalizedDwUsers = rawDwUsers
      .map((user: any) => this.normalizeDwUserOption(user))
      .filter((user: any) => user != null);

    const savedExistingLayout = currentSiteData?.existing_layout ?? currentSiteData?.DW_layout ?? null;
    this.pendingLayoutId =
      `${currentSiteData?.layout_id ?? savedExistingLayout?.id ?? savedExistingLayout?.value ?? savedExistingLayout?._id ?? ''}`.trim() || null;
    this.pendingLayoutName =
      `${currentSiteData?.layout_name ?? savedExistingLayout?.name ?? savedExistingLayout?.label ?? savedExistingLayout?.layout_name ?? ''}`.trim() || null;

    const collectedUserIds = this.collectSavedUserIds(currentSiteData, rawDwUsers);
    if (collectedUserIds.length > 0) {
      this.pendingDwUserIds = collectedUserIds;
    }
    if (normalizedDwUsers.length > 0) {
      this.pendingDwUserRefs = normalizedDwUsers;
    }

    const rawSavedEvents =
      currentSiteData?.DW_events ??
      currentSiteData?.dw_events ??
      (currentSiteData?.DW_event != null ? [currentSiteData.DW_event] : null) ??
      (currentSiteData?.dw_event != null ? [currentSiteData.dw_event] : null);
    if (Array.isArray(rawSavedEvents) && rawSavedEvents.length > 0) {
      this.pendingDwEventIds = rawSavedEvents
        .map((ev: any) =>
          typeof ev === 'object' && ev !== null
            ? `${ev?.value ?? ev?.id ?? ev?.eventId ?? ev?.type ?? ''}`.trim()
            : `${ev ?? ''}`.trim(),
        )
        .filter((id: string) => id !== '');
    }

    const rawSavedActions =
      currentSiteData?.DW_actions ??
      currentSiteData?.dw_actions ??
      (currentSiteData?.DW_action != null ? [currentSiteData.DW_action] : null) ??
      (currentSiteData?.dw_action != null ? [currentSiteData.dw_action] : null);
    if (Array.isArray(rawSavedActions) && rawSavedActions.length > 0) {
      this.pendingDwActionIds = rawSavedActions
        .map((ac: any) =>
          typeof ac === 'object' && ac !== null
            ? `${ac?.value ?? ac?.id ?? ac?.actionId ?? ac?.type ?? ''}`.trim()
            : `${ac ?? ''}`.trim(),
        )
        .filter((id: string) => id !== '');
    }

    this.addSiteForm.patchValue({
      _id: currentSiteData?._id ?? '',
      site_name: currentSiteData?.site_name ?? '',
      display_name: currentSiteData?.display_name ?? '',
      CRM_url: currentSiteData?.CRM_url ?? '',
      client_contact_number: currentSiteData?.client_contact_number ?? currentSiteData?.clientContactNumber ?? '',
      site_type: normalizeSiteType(currentSiteData?.site_type) || null,
      emergency_call_number: currentSiteData?.emergency_call_number ?? '',
      layout_option: 'existing',
      layout_name: currentSiteData?.layout_name ?? '',
      DW_server: this.normalizeSavedServer(currentSiteData?.DW_server ?? currentSiteData?.dw_server ?? null),
    });

    this.setSpeakerNumbers(currentSiteData);
    this.setResponseProtocol(currentSiteData);

    const existingDevices = currentSiteData?.DW_devices ?? currentSiteData?.devices ?? [];
    if (Array.isArray(existingDevices) && existingDevices.length > 0) {
      this.devices.clear();
      existingDevices.forEach((device: any) => {
        this.devices.push(this.fb.group({
          name: [device?.name ?? ''],
          deviceType: [device?.typeId ?? device?.deviceType ?? device?.device_type ?? device?.type ?? null],
          DW_ip_address: [device?.DW_ip_address ?? currentSiteData?.DW_ip_address ?? currentSiteData?.ip_address ?? ''],
          DW_port: [device?.DW_port ?? currentSiteData?.DW_port ?? currentSiteData?.port ?? ''],
          DW_username: [device?.DW_username ?? currentSiteData?.DW_username ?? currentSiteData?.username ?? ''],
          DW_password: [device?.DW_password ?? currentSiteData?.DW_password ?? currentSiteData?.password ?? ''],
          DW_description: [device?.DW_description ?? currentSiteData?.DW_description ?? currentSiteData?.description ?? ''],
          DW_source: [device?.DW_source ?? currentSiteData?.DW_source ?? currentSiteData?.source ?? ''],
        }));
      });
      this.deviceSequence = existingDevices.length + 1;
    }

    this.disableEditModeFields();
    this.siteDataInitialized = true;
    this.tryApplyDwUsers();
  }

  get isNewLayoutOption(): boolean {
    return this.addSiteForm.get('layout_option')?.value === 'new';
  }

  private normalizeGuid(value: any): string {
    return `${value ?? ''}`.trim().replace(/^\{|\}$/g, '').toLowerCase();
  }

  private applyPendingExistingLayout(): void {
    const siteData = this.siteData as any;
    const savedLayout = siteData?.existing_layout ?? siteData?.DW_layout ?? null;
    const layoutId = this.pendingLayoutId;
    const layoutName = this.pendingLayoutName;

    if (savedLayout && typeof savedLayout === 'object') {
      const savedId = `${savedLayout?.id ?? savedLayout?.value ?? savedLayout?._id ?? ''}`.trim();
      const matchedSaved = this.dwLayouts.find(
        (layout) =>
          (savedId && this.normalizeGuid(layout.id) === this.normalizeGuid(savedId)) ||
          (layoutName && `${layout.name ?? layout.label ?? ''}`.trim().toLowerCase() === layoutName.toLowerCase()),
      );
      if (matchedSaved) {
        this.addSiteForm.patchValue({ existing_layout: matchedSaved }, { emitEvent: false });
        return;
      }
    }

    if (layoutId) {
      const matchedLayout = this.dwLayouts.find(
        (layout) => this.normalizeGuid(layout.id) === this.normalizeGuid(layoutId)
          || this.normalizeGuid(layout.value) === this.normalizeGuid(layoutId),
      );
      if (matchedLayout) {
        this.addSiteForm.patchValue({ existing_layout: matchedLayout }, { emitEvent: false });
        return;
      }
    }

    if (layoutName) {
      const matchedByName = this.dwLayouts.find(
        (layout) =>
          `${layout.name ?? layout.label ?? ''}`.trim().toLowerCase() === layoutName.toLowerCase(),
      );
      if (matchedByName) {
        this.addSiteForm.patchValue({ existing_layout: matchedByName }, { emitEvent: false });
        return;
      }

      this.addSiteForm.patchValue({
        existing_layout: {
          id: layoutId || layoutName,
          name: layoutName,
          label: layoutName,
          value: layoutId || layoutName,
        },
      }, { emitEvent: false });
    }
  }

  private normalizeDwUserOption(user: any): any | null {
    if (user == null || `${user}`.trim() === '') {
      return null;
    }

    if (typeof user !== 'object') {
      const id = `${user}`.trim();
      return { label: id, value: id };
    }

    const value = user?.value ?? user?._id ?? user?.id ?? user?.user_id ?? user?.email ?? null;
    const label =
      user?.label ??
      user?.name ??
      user?.username ??
      user?.user_name ??
      user?.email ??
      (user?.first_name || user?.last_name
        ? `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
        : '') ??
      `${value ?? ''}`;

    return { ...user, label, value };
  }

  private normalizeSavedServer(server: any): any {
    if (!server) {
      return null;
    }

    if (typeof server !== 'object') {
      const id = `${server}`.trim();
      return id ? { id, value: id, label: id, name: id } : null;
    }

    const id = `${server?.id ?? server?._id ?? server?.serverId ?? server?.value ?? ''}`.trim();
    const name =
      server?.name ??
      server?.server_name ??
      server?.displayName ??
      server?.display_name ??
      server?.hostname ??
      server?.label ??
      id;

    return {
      ...server,
      id,
      name,
      label: name || id || 'Server',
      value: id || name,
    };
  }

  private setupSiteNameListener(): void {
    if (this.isEditMode) {
      return;
    }
    this.addSiteForm.get('site_name')?.valueChanges.subscribe(() => {
      this.syncDeviceDescriptions();
    });
  }

  private setupDwUserListener(): void {
    if (this.isEditMode) {
      return;
    }
    this.addSiteForm.get('DW_user')?.valueChanges.subscribe(() => {
      this.syncDeviceDescriptions();
    });
  }

  private buildDeviceDescription(): string {
    const siteName = `${this.addSiteForm.get('site_name')?.value ?? ''}`.replace(/\s+/g, '');
    const dwUsersRaw = this.addSiteForm.get('DW_user')?.value;
    const dwUsersList = Array.isArray(dwUsersRaw) ? dwUsersRaw : (dwUsersRaw ? [dwUsersRaw] : []);

    const dwUserParts = dwUsersList
      .map((user) => {
        const normalized = this.normalizeDwUserOption(user);
        const label = normalized?.label ?? `${user ?? ''}`;
        return `${label}`.replace(/\s+/g, '').toLowerCase();
      })
      .filter((part) => part.length > 0);

    return [siteName, ...dwUserParts].filter((part) => part.length > 0).join('-');
  }

  private syncDeviceDescriptions(): void {
    const description = this.buildDeviceDescription();
    this.devices.controls.forEach((deviceGroup) => {
      deviceGroup.patchValue({ DW_description: description }, { emitEvent: false });
    });
  }

  private setupLayoutOptionListener(): void {
    this.addSiteForm.get('layout_option')?.valueChanges.subscribe((option) => {
      if (option === 'new') {
        this.addSiteForm.patchValue({ existing_layout: null }, { emitEvent: false });
        this.resetDevicesForNewLayout();
      } else {
        this.addSiteForm.patchValue({
          layout_name: '',
          DW_user: null,
          DW_events: null,
          DW_actions: null,
        }, { emitEvent: false });
        this.clearDevices();
        if (!this.isEditMode) {
          this.syncDeviceDescriptions();
        }
      }
    });
  }

  private clearDevices(): void {
    this.devices.clear();
    this.deviceSequence = 1;
  }

  private resetDevicesForNewLayout(): void {
    if (this.devices.length > 0) {
      return;
    }
    this.deviceSequence = 1;
    this.addDevice();
  }

  private getDeviceIdsFromLayout(layout: any): string[] {
    if (!layout) {
      return [];
    }

    const items = Array.isArray(layout?.items)
      ? layout.items
      : (Array.isArray(layout?.raw?.items) ? layout.raw.items : []);
    const seen = new Set<string>();
    const deviceIds: string[] = [];

    for (const item of items) {
      const id = `${item?.resourceId ?? item?.deviceId ?? item?.device_id ?? ''}`.trim();
      if (id && !seen.has(id)) {
        seen.add(id);
        deviceIds.push(id);
      }
    }

    return deviceIds;
  }

  private getUserIdsFromSelection(users: any): string[] {
    const list = Array.isArray(users) ? users : (users ? [users] : []);
    const seen = new Set<string>();
    const userIds: string[] = [];

    for (const user of list) {
      const id = `${user?.id ?? user?._id ?? user?.user_id ?? user?.value ?? ''}`.trim();
      if (id && !seen.has(id)) {
        seen.add(id);
        userIds.push(id);
      }
    }

    return userIds;
  }

  private getUserEnabledAnalyticsEngineIdsFromSelection(users: any): string[] {
    const list = Array.isArray(users) ? users : (users ? [users] : []);
    const seen = new Set<string>();
    const engineIds: string[] = [];

    for (const user of list) {
      const userEngineIds = Array.isArray(user?.userEnabledAnalyticsEngineIds)
        ? user.userEnabledAnalyticsEngineIds
        : [];
      const idsToAdd = userEngineIds.length > 0
        ? userEngineIds
        : [`${user?.id ?? user?._id ?? user?.user_id ?? user?.value ?? ''}`.trim()];

      for (const id of idsToAdd) {
        const trimmed = `${id ?? ''}`.trim();
        if (trimmed && !seen.has(trimmed)) {
          seen.add(trimmed);
          engineIds.push(trimmed);
        }
      }
    }

    return engineIds;
  }

  loadDwLayouts() {
    this.isDwLayoutsLoading = true;
    this.dwLayoutsSubscription = this._dwService.getLayoutList().subscribe({
      next: (res: any) => {
        const rawList = Array.isArray(res)
          ? res
          : (res?.data ?? res?.list ?? res?.items ?? res?.result ?? []);

        const seen = new Set<string>();
        this.dwLayouts = (Array.isArray(rawList) ? rawList : []).map((layout: any) => {
          const id = `${layout?.id ?? layout?._id ?? ''}`.trim();
          const name =
            layout?.name ??
            layout?.layout_name ??
            layout?.displayName ??
            layout?.display_name ??
            id;
          return {
            ...layout,
            id,
            name,
            label: name || id || 'Layout',
            value: id || name,
            raw: layout,
          };
        }).filter((layout) => {
          if (!layout.value || seen.has(layout.value)) {
            return false;
          }
          seen.add(layout.value);
          return true;
        });

        this.applyPendingExistingLayout();
        this.pendingLayoutId = null;
        this.pendingLayoutName = null;

        this.isDwLayoutsLoading = false;
      },
      error: (e) => {
        this.isDwLayoutsLoading = false;
        console.log(e);
      },
      complete: () => {
        if (this.dwLayoutsSubscription) {
          this.dwLayoutsSubscription.unsubscribe();
        }
      },
    });
  }

  loadDwServers() {
    this.isDwServersLoading = true;
    this.dwServersSubscription = this._dwService.getServerList().subscribe({
      next: (res: any) => {
        const rawList = Array.isArray(res)
          ? res
          : (res?.servers ?? res?.data ?? res?.list ?? res?.items ?? res?.result ?? []);

        const seen = new Set<string>();
        this.dwServers = (Array.isArray(rawList) ? rawList : []).map((server: any) => {
          const id = `${server?.id ?? server?._id ?? server?.serverId ?? ''}`.trim();
          const name =
            server?.name ??
            server?.server_name ??
            server?.displayName ??
            server?.display_name ??
            server?.hostname ??
            id;
          return {
            ...server,
            id,
            name,
            label: name || id || 'Server',
            value: id || name,
            raw: server,
          };
        }).filter((server) => {
          if (!server.value || seen.has(server.value)) {
            return false;
          }
          seen.add(server.value);
          return true;
        });

        const currentRaw = this.addSiteForm.get('DW_server')?.value;
        const current = this.normalizeSavedServer(currentRaw);
        const currentId = current?.id ?? current?.value ?? null;

        if (currentId) {
          const match = this.dwServers.find((server) => server.value === currentId || server.id === currentId);
          if (match) {
            this.addSiteForm.patchValue({ DW_server: match }, { emitEvent: false });
          } else if (current) {
            this.addSiteForm.patchValue({ DW_server: current }, { emitEvent: false });
          }
        }

        this.isDwServersLoading = false;
      },
      error: (e) => {
        this.isDwServersLoading = false;
        console.log(e);
      },
      complete: () => {
        if (this.dwServersSubscription) {
          this.dwServersSubscription.unsubscribe();
        }
      },
    });
  }

  private disableEditModeFields(): void {
    const controlsToDisable = ['layout_option', 'layout_name', 'DW_server', 'DW_events', 'DW_actions'];
    controlsToDisable.forEach((controlName) => {
      this.addSiteForm.get(controlName)?.disable({ emitEvent: false });
    });
    this.devices.controls.forEach((deviceGroup) => {
      deviceGroup.disable({ emitEvent: false });
    });
  }

  get devices(): UntypedFormArray {
    return this.addSiteForm.get('DW_devices') as UntypedFormArray;
  }

  get speakerNumbers(): UntypedFormArray {
    return this.addSiteForm.get('speaker_numbers') as UntypedFormArray;
  }

  // Bound with [formControl] (not formControlName + $index): the rows are added,
  // removed and rebuilt after the view has rendered, and an index-based binding
  // stays attached to the control that used to sit at that index.
  get speakerNumberControls(): UntypedFormControl[] {
    return this.speakerNumbers.controls as UntypedFormControl[];
  }

  addSpeakerNumber(): void {
    this.speakerNumbers.push(this.fb.control(''));
  }

  // Always keep one (blank) row so the field never disappears entirely.
  removeSpeakerNumber(idx: number): void {
    if (this.speakerNumbers.length <= 1) {
      this.speakerNumbers.at(0)?.setValue('');
      return;
    }
    this.speakerNumbers.removeAt(idx);
  }

  // A speaker picked from the PBX search: its extension fills the first blank row, or a
  // new one. Already listed numbers are left alone.
  addPbxSpeaker(candidate: PbxSpeakerPick): void {
    const extension = `${candidate?.extension ?? ''}`.trim();
    if (!extension) {
      return;
    }
    this.rememberPbxCamera(candidate);
    const values = this.speakerNumberControls.map((ctrl) => `${ctrl.value ?? ''}`.trim());
    if (values.includes(extension)) {
      return;
    }
    const blank = this.speakerNumberControls.find((ctrl) => `${ctrl.value ?? ''}`.trim() === '');
    if (blank) {
      blank.setValue(extension);
    } else {
      this.speakerNumbers.push(this.fb.control(extension));
    }
    this.speakerNumbers.markAsDirty();
  }

  // Latest camera per speaker. A cleared camera drops a new speaker from the save, but is
  // kept for an existing record so the save can take the camera off it.
  rememberPbxCamera(pick: PbxSpeakerPick): void {
    this.pbxSpeakerPicks = this.pbxSpeakerPicks
      .filter((p) => !(p.provider === pick.provider && p.extension === pick.extension))
      .concat(pick.camera_id || pick.speaker_id ? [pick] : []);
  }

  // Only a saved site has cameras to pick from.
  get pbxSuggestSiteId(): string | null {
    return this.isEditMode ? (`${(this.siteData as any)?._id ?? ''}` || null) : null;
  }

  // A speaker with a camera becomes a Speaker record, which is what links it to that
  // camera's alerts. Picks whose number was removed from the form before saving are dropped.
  private savePbxSpeakerRecords(siteId: string, savedNumbers: string[]): void {
    const picks = this.pbxSpeakerPicks.filter((p) => savedNumbers.includes(p.extension));
    this.pbxSpeakerPicks = [];
    picks.forEach((pick) => {
      if (pick.speaker_id) {
        // Only the camera list changes; the record's name, sites and PBX link stay.
        const cameras = (pick.other_camera_ids || []).concat(pick.camera_id ? [pick.camera_id] : []);
        this._alertService.editSpeakerDetails({ camera: cameras }, pick.speaker_id).subscribe({
          error: (e) => console.log(e)
        });
        return;
      }
      if (!pick.camera_id) {
        return;
      }
      this._alertService.addNewSpeaker({
        name: pick.name,
        description: '',
        call_number: pick.extension,
        site: [siteId],
        camera: [pick.camera_id],
        pbx_provider: pick.provider,
        pbx_extension_id: pick.provider === 'vitalpbx' ? pick.pbx_extension_id : null,
      }).subscribe({
        error: (e) => console.log(e)
      });
    });
  }

  // Saved value may be an array, a legacy single string, or comma-separated.
  private setSpeakerNumbers(currentSiteData: any): void {
    const raw = currentSiteData?.speaker_numbers ?? currentSiteData?.speaker_number ?? [];
    const list = (Array.isArray(raw) ? raw : `${raw}`.split(','))
      .map((num: any) => `${num ?? ''}`.trim())
      .filter((num: string) => num !== '');

    this.speakerNumbers.clear();
    if (list.length === 0) {
      this.speakerNumbers.push(this.fb.control(''));
      return;
    }
    list.forEach((num: string) => this.speakerNumbers.push(this.fb.control(num)));
  }

  addDevice() {
    const defaultDeviceName = `Device ${this.deviceSequence}`;
    this.deviceSequence += 1;
    const defaultDescription = this.buildDeviceDescription();

    this.devices.push(this.fb.group({
      name: [defaultDeviceName],
      deviceType: [null],
      DW_ip_address: [''],
      DW_port: [''],
      DW_username: [''],
      DW_password: [''],
      DW_description: [defaultDescription],
      DW_source: [''],
    }));
    if (!this.isEditMode) {
      const defaultType = this.findDefaultCameraDeviceType();
      if (defaultType) {
        this.devices.at(this.devices.length - 1).patchValue(
          { deviceType: defaultType.value },
          { emitEvent: false },
        );
      }
    }
  }

  resolveDeviceTypeValue(input: string | null | undefined): string | null {
    if (input == null || `${input}`.trim() === '') {
      return null;
    }

    const trimmed = `${input}`.trim();
    const match = this.deviceTypes.find(
      (t) =>
        `${t.label}`.toLowerCase() === trimmed.toLowerCase() ||
        `${t.value}`.toLowerCase() === trimmed.toLowerCase(),
    );

    return match?.value ?? trimmed;
  }

  private ensureDeviceTypeInList(typeId: string | number): { label: string; value: any } {
    const normalized = `${typeId}`.trim();
    const match = this.deviceTypes.find(
      (t) =>
        t?.value === typeId ||
        `${t?.label ?? ''}`.toLowerCase() === normalized.toLowerCase() ||
        `${t?.name ?? ''}`.toLowerCase() === normalized.toLowerCase() ||
        `${t?.type ?? ''}`.toLowerCase() === normalized.toLowerCase(),
    );
    if (match) {
      return match;
    }

    const customType = { label: normalized, value: typeId, isCustom: true };
    this.deviceTypes = [...this.deviceTypes, customType];
    return customType;
  }

  private findDefaultCameraDeviceType(): { label: string; value: any } | undefined {
    return this.deviceTypes.find(
      (t) =>
        `${t.label}`.toLowerCase() === 'camera' ||
        `${t.value}`.toLowerCase() === 'camera' ||
        `${t.name ?? ''}`.toLowerCase() === 'camera' ||
        `${t.type ?? ''}`.toLowerCase() === 'camera',
    );
  }

  removeDevice(idx: number) {
    if (this.devices.length <= 1) return;
    this.devices.removeAt(idx);
  }

  private getDwUserRefKey(user: any): string {
    const id = user?.value ?? user?.id ?? user?._id ?? user?.user_id ?? '';
    if (id) {
      return `id:${this.normalizeGuid(id)}`;
    }
    const name = `${user?.name ?? user?.label ?? user?.username ?? user?.user_name ?? ''}`.trim().toLowerCase();
    return name ? `name:${name}` : '';
  }

  private findDwUserInList(ref: any): any | null {
    if (ref == null || `${ref}`.trim() === '') {
      return null;
    }

    const refId = typeof ref === 'object'
      ? (ref?.value ?? ref?.id ?? ref?._id ?? ref?.user_id ?? ref?.email)
      : ref;
    const refName = typeof ref === 'object'
      ? `${ref?.name ?? ref?.label ?? ref?.username ?? ref?.user_name ?? ''}`.trim().toLowerCase()
      : '';

    return this.dwUsers.find((user) => {
      if (refId) {
        const refIdNormalized = this.normalizeGuid(refId);
        if (
          this.normalizeGuid(user?.value) === refIdNormalized
          || this.normalizeGuid(user?.id) === refIdNormalized
          || this.normalizeGuid(user?._id) === refIdNormalized
        ) {
          return true;
        }
      }
      if (refName) {
        const userName = `${user?.name ?? user?.label ?? user?.username ?? user?.user_name ?? ''}`.trim().toLowerCase();
        return userName === refName;
      }
      return false;
    }) ?? null;
  }

  private ensureDwUserInList(userRef: any): any | null {
    const match = this.findDwUserInList(userRef);
    if (match) {
      return match;
    }

    const normalized = typeof userRef === 'object'
      ? this.normalizeDwUserOption(userRef)
      : this.normalizeDwUserOption({ value: userRef, label: `${userRef}` });
    if (!normalized) {
      return null;
    }

    const key = this.getDwUserRefKey(normalized);
    if (!key) {
      return null;
    }

    const existing = this.dwUsers.find((user) => this.getDwUserRefKey(user) === key);
    if (existing) {
      return existing;
    }

    this.dwUsers = [...this.dwUsers, normalized];
    return normalized;
  }

  private dedupeDwUsers(users: any[]): any[] {
    const seen = new Set<string>();
    const result: any[] = [];

    for (const user of users) {
      const key = this.getDwUserRefKey(user);
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push(user);
    }

    return result;
  }

  private tryApplyDwUsers(): void {
    if (!this.siteDataInitialized || !this.dwUsersLoaded) {
      return;
    }
    this.applyDwUserSelection();
  }

  private applyDwUserSelection(): void {
    const userRefs: any[] = [];

    if (this.pendingDwUserIds?.length) {
      userRefs.push(...this.pendingDwUserIds);
    }
    if (this.pendingDwUserRefs?.length) {
      userRefs.push(...this.pendingDwUserRefs);
    }

    const currentRaw = this.addSiteForm.get('DW_user')?.value;
    const currentList = Array.isArray(currentRaw)
      ? currentRaw
      : (currentRaw ? [currentRaw] : []);
    if (!userRefs.length && currentList.length) {
      userRefs.push(...currentList);
    }

    if (!userRefs.length) {
      return;
    }

    const matchedUsers = this.dedupeDwUsers(
      userRefs
        .map((ref) => this.ensureDwUserInList(ref))
        .filter((user): user is NonNullable<typeof user> => !!user),
    );

    if (matchedUsers.length) {
      this.addSiteForm.patchValue(
        { DW_user: matchedUsers },
        { emitEvent: false },
      );
      this.pendingDwUserIds = null;
      this.pendingDwUserRefs = null;
      if (!this.isEditMode) {
        this.syncDeviceDescriptions();
      }
    }
  }

  loadDwUsers() {
    this.isDwUsersLoading = true;
    this.dwUsersSubscription = this._userService.getDwUserList().subscribe({
      next: (res: any) => {
        const rawList = Array.isArray(res)
          ? res
          : (res?.users ?? res?.userList ?? res?.data ?? res?.result ?? res?.list ?? []);

        this.dwUsers = (Array.isArray(rawList) ? rawList : []).map((u: any) => {
          const label =
            u?.name ??
            u?.username ??
            u?.user_name ??
            u?.email ??
            (u?.first_name || u?.last_name ? `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() : '') ??
            `${u?._id ?? u?.id ?? ''}`;

          const value = u?._id ?? u?.id ?? u?.user_id ?? u?.email ?? label;

          return { ...u, label, value };
        });

        this.dwUsersLoaded = true;
        this.tryApplyDwUsers();
        this.isDwUsersLoading = false;
      },
      error: (e) => {
        this.dwUsersLoaded = true;
        this.isDwUsersLoading = false;
        this.tryApplyDwUsers();
        console.log(e);
      },
      complete: () => {
        if (this.dwUsersSubscription) this.dwUsersSubscription.unsubscribe();
      }
    });
  }

  private normalizeManifestToEventRows(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }
    const candidates = [
      res?.events,
      res?.items,
      res?.list,
      res?.data,
      res?.result,
      res?.eventTypes,
      res?.manifest,
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) {
        return c;
      }
      if (c && typeof c === 'object' && !Array.isArray(c)) {
        return Object.entries(c).map(([key, val]) =>
          typeof val === 'object' && val !== null ? { ...val, id: (val as any).id ?? key } : { id: key, name: val },
        );
      }
    }
    return [];
  }

  private normalizeManifestToActionRows(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }
    const candidates = [
      res?.actions,
      res?.actionTypes,
      res?.items,
      res?.list,
      res?.data,
      res?.result,
      res?.manifest,
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) {
        return c;
      }
      if (c && typeof c === 'object' && !Array.isArray(c)) {
        return Object.entries(c).map(([key, val]) =>
          typeof val === 'object' && val !== null ? { ...val, id: (val as any).id ?? key } : { id: key, name: val },
        );
      }
    }
    return [];
  }

  loadDwEvents() {
    this.isDwEventsLoading = true;
    this.dwEventsSubscription = this._userService.getDwEventsManifest().subscribe({
      next: (res: any) => {
        const rawList = this.normalizeManifestToEventRows(res);
        const seen = new Set<string>();
        this.dwEvents = (Array.isArray(rawList) ? rawList : []).map((ev: any) => {
          const value = `${ev?.value ?? ev?.id ?? ev?.eventId ?? ev?.type ?? ev?.name ?? ev?.title ?? ''}`.trim();
          const label =
            ev?.label ??
            ev?.name ??
            ev?.title ??
            ev?.displayName ??
            ev?.type ??
            ev?.id ??
            value;
          return { label: label || value || 'Event', value: value || `${label}`, raw: ev };
        }).filter((e) => {
          if (e.value === '' || seen.has(e.value)) {
            return false;
          }
          seen.add(e.value);
          return true;
        });

        const pending = this.pendingDwEventIds;
        if (pending?.length) {
          const known = new Set(this.dwEvents.map((e) => e.value));
          const patched = pending.filter((id) => known.has(id));
          this.addSiteForm.patchValue(
            { DW_events: patched.length ? patched : pending },
            { emitEvent: false },
          );
          this.pendingDwEventIds = null;
        } else if (!this.isEditMode) {
          const defaultEvent = this.dwEvents.find(
            (e) => `${e.label}`.toLowerCase() === 'generic event' || `${e.value}`.toLowerCase() === 'generic event',
          );
          if (defaultEvent) {
            this.addSiteForm.patchValue({ DW_events: defaultEvent.value }, { emitEvent: false });
          }
        }
        this.isDwEventsLoading = false;
      },
      error: (e) => {
        this.isDwEventsLoading = false;
        console.log(e);
      },
      complete: () => {
        if (this.dwEventsSubscription) {
          this.dwEventsSubscription.unsubscribe();
        }
      },
    });
  }

  loadDwActions() {
    this.isDwActionsLoading = true;
    this.dwActionsSubscription = this._userService.getDwEventManifestActions().subscribe({
      next: (res: any) => {
        const rawList = this.normalizeManifestToActionRows(res);
        const seen = new Set<string>();
        this.dwActions = (Array.isArray(rawList) ? rawList : []).map((ac: any) => {
          const value = `${ac?.value ?? ac?.actionId ?? ac?.id ?? ac?.type ?? ac?.name ?? ac?.title ?? ''}`.trim();
          const label =
            ac?.label ??
            ac?.name ??
            ac?.title ??
            ac?.displayName ??
            ac?.type ??
            ac?.actionId ??
            ac?.id ??
            value;
          return { label: label || value || 'Action', value: value || `${label}`, raw: ac };
        }).filter((e) => {
          if (e.value === '' || seen.has(e.value)) {
            return false;
          }
          seen.add(e.value);
          return true;
        });

        const pending = this.pendingDwActionIds;
        if (pending?.length) {
          const known = new Set(this.dwActions.map((e) => e.value));
          const patched = pending.filter((id) => known.has(id));
          this.addSiteForm.patchValue(
            { DW_actions: patched.length ? patched : pending },
            { emitEvent: false },
          );
          this.pendingDwActionIds = null;
        } else if (!this.isEditMode) {
          const defaultAction = this.dwActions.find(
            (e) => `${e.label}`.toLowerCase() === 'open layout' || `${e.value}`.toLowerCase() === 'open layout',
          );
          if (defaultAction) {
            this.addSiteForm.patchValue({ DW_actions: defaultAction.value }, { emitEvent: false });
          }
        }
        this.isDwActionsLoading = false;
      },
      error: (e) => {
        this.isDwActionsLoading = false;
        console.log(e);
      },
      complete: () => {
        if (this.dwActionsSubscription) {
          this.dwActionsSubscription.unsubscribe();
        }
      },
    });
  }

  loadDeviceTypes() {
    this.isDeviceTypesLoading = true;
    this.dwDeviceTypesSubscription = this._userService.getDwDeviceTypes().subscribe({
      next: (res: any) => {
        const rawList = Array.isArray(res)
          ? res
          : (res?.deviceTypes ?? res?.types ?? res?.data ?? res?.result ?? res?.list ?? []);

        this.deviceTypes = (Array.isArray(rawList) ? rawList : []).map((t: any) => {
          const label =
            t?.name ??
            t?.type_name ??
            t?.device_type ??
            t?.type ??
            `${t?._id ?? t?.id ?? t?.value ?? ''}`;
          const value = t?.value ?? t?._id ?? t?.id ?? t?.device_type ?? t?.type ?? label;
          return { ...t, label, value };
        });

        this.devices.controls.forEach((deviceControl) => {
          const currentType = deviceControl.get('deviceType')?.value;
          const currentTypeId = typeof currentType === 'object'
            ? (currentType?.value ?? currentType?._id ?? currentType?.id ?? currentType?.device_type ?? currentType?.type)
            : currentType;

          if (currentTypeId) {
            const match = this.ensureDeviceTypeInList(currentTypeId);
            deviceControl.patchValue({ deviceType: match.value }, { emitEvent: false });
          } else if (!this.isEditMode) {
            const defaultType = this.findDefaultCameraDeviceType();
            if (defaultType) {
              deviceControl.patchValue({ deviceType: defaultType.value }, { emitEvent: false });
            }
          }
        });
        this.isDeviceTypesLoading = false;
      },
      error: (e) => {
        this.isDeviceTypesLoading = false;
        console.log(e);
      },
      complete: () => {
        if (this.dwDeviceTypesSubscription) this.dwDeviceTypesSubscription.unsubscribe();
      }
    });
  }

  private resolveRequestDescription(formValue: any): string {
    const builtDescription = this.buildDeviceDescription();
    if (builtDescription) {
      return builtDescription;
    }

    const firstDevice = (formValue?.DW_devices ?? [])[0];
    const siteData = this.siteData as any;
    return firstDevice?.DW_description ?? siteData?.description ?? siteData?.DW_description ?? '';
  }

  closeModal() {
    if (this.embedded) {
      this._siteService.closeSiteDrawer();
      return;
    }
    this.modalService.hide();
  }

  /**
   * Clears the form and closes it. Split out of the save handler because the Alert Schedule
   * drawer needs the form to stay open behind it, and the close has to wait for that.
   */
  private finishSiteCreation(): void {
    // A save can have added the site's first camera or changed its url. The dashboard reads
    // its camera list from a cached site list, which would otherwise go on answering with the
    // pre-save picture — and an alert for a brand new site would show no live view at all.
    // Already done for the live-url rows; needed on add too, where those never run.
    this._rtspCameraService.clearCache();
    this.addSiteForm.reset();
    this.alertScheduleDrafts = [];
    this.alertScheduleSubmitted = false;
    if (this.embedded) {
      // Saved, so the list behind the drawer is now out of date and worth refetching.
      this._siteService.closeSiteDrawer(true);
    } else {
      this.modalService.setDismissReason('Success');
      this.modalService.hide();
    }
  }

  addSite() {
    this._utils.markFormGroupDirty(this.addSiteForm);

    // The visitor schedule is not re-checked here. Site Settings validates it in its own
    // drawer, when Save there is pressed, and nowhere else — checking it again on the site
    // form put errors in front of the user for a panel they had already closed.
    //
    // Alert Schedule IS required, but only when creating a site: on an existing one its
    // schedules already live server-side and aren't reflected in the draft array, so the
    // same check in edit mode would block a save that has nothing wrong with it.
    this.alertScheduleSubmitted = true;
    const alertScheduleMissing = !this.isEditMode && !this.alertScheduleDrafts.length;

    if (this.addSiteForm.valid && !alertScheduleMissing) {
      this.isLoading = true;
      const formValue = this.addSiteForm.getRawValue();
      const layoutOption = this.isEditMode ? 'existing' : (formValue?.layout_option ?? 'new');
      const selectedExistingLayout = formValue?.existing_layout;
      const selectedServer = formValue?.DW_server;
      const resolvedServerId = selectedServer?.id ?? selectedServer?.value ?? selectedServer?._id ?? selectedServer?.serverId ?? null;
      const resolvedServerName = selectedServer?.name ?? selectedServer?.label ?? '';
      const resolvedLayoutName = layoutOption === 'existing'
        ? (selectedExistingLayout?.name ?? selectedExistingLayout?.label ?? '')
        : (formValue?.layout_name ?? '');
      const resolvedLayoutId = layoutOption === 'existing'
        ? (selectedExistingLayout?.id ?? selectedExistingLayout?.value ?? null)
        : null;
      const resolvedDeviceIds = layoutOption === 'existing'
        ? this.getDeviceIdsFromLayout(selectedExistingLayout)
        : [];
      const isNewLayout = layoutOption === 'new';
      const selectedDwUsers = Array.isArray(formValue?.DW_user)
        ? formValue.DW_user
        : (formValue?.DW_user ? [formValue.DW_user] : []);
      const resolvedUserIds = this.getUserIdsFromSelection(selectedDwUsers);
      const dwUserValue = selectedDwUsers.length <= 1
        ? (selectedDwUsers[0] ?? null)
        : selectedDwUsers;
      const userEnabledAnalyticsEngineIds = this.getUserEnabledAnalyticsEngineIdsFromSelection(selectedDwUsers);
      const firstDevice = (formValue?.DW_devices ?? [])[0];
      // assigned_teams is persisted via team_hierarchy, not the site API — keep it out of the payload.
      const selectedTeamIds = (formValue?.assigned_teams ?? []).map((id: any) => `${id}`);
      const currentSiteName = `${formValue?.site_name ?? ''}`.trim();
      const {
        existing_layout, assigned_teams,
        is_schedule_active, is_alert_active, is_alert_email_send_active,
        receive_alert_send_email, is_archive_alert_active,
        is_face_detection, is_vehicle_detection, is_zone_detection, is_rtsp_recording,
        ...restFormValue
      } = formValue;
      // Site option toggles are set only when creating a site. In edit mode these
      // are managed via the Settings / Cameras drawers, so we don't touch them here.
      const siteFlags = this.isEditMode ? {} : {
        is_schedule_active: !!is_schedule_active,
        is_alert_active: !!is_alert_active,
        is_alert_email_send_active: !!is_alert_email_send_active,
        receive_alert_send_email: is_alert_email_send_active ? (receive_alert_send_email ?? '') : '',
        is_archive_alert_active: !!is_archive_alert_active,
        is_face_detection: !!is_face_detection,
        is_vehicle_detection: !!is_vehicle_detection,
        is_zone_detection: !!is_zone_detection,
        is_rtsp_recording: !!is_rtsp_recording,
      };
      const requestPayload = {
        ...restFormValue,
        ...siteFlags,
        layout_option: layoutOption,
        layout_name: resolvedLayoutName,
        layout_id: resolvedLayoutId,
        server_id: resolvedServerId,
        server_name: resolvedServerName,
        DW_server: selectedServer,
        deviceIds: resolvedDeviceIds,
        userIds: resolvedUserIds,
        DW_user: dwUserValue,
        DW_events: isNewLayout ? formValue?.DW_events : null,
        DW_actions: isNewLayout ? formValue?.DW_actions : null,
        speaker_numbers: (formValue?.speaker_numbers ?? [])
          .map((num: any) => `${num ?? ''}`.trim())
          .filter((num: string) => num !== ''),
        // Sent on both create and edit. null is deliberate and meaningful — it is how a
        // site stops notifying anybody.
        emergency_call_number: `${formValue?.emergency_call_number ?? ''}`.trim(),
        // Trimmed like the 911 number beside it — a stray space is not part of a phone number.
        client_contact_number: `${formValue?.client_contact_number ?? ''}`.trim(),
        // '' rather than null so clearing the picker unclassifies the site instead of being
        // read as "not sent".
        site_type: formValue?.site_type ?? '',
        response_protocol: {
          use_default: this.responseUseDefault,
          steps: this.responseUseDefault ? [] : this.responseSteps,
        },
        source: firstDevice?.DW_source ?? (this.isEditMode ? ((this.siteData as any)?.source ?? '') : ''),
        description: this.resolveRequestDescription(formValue),
        DW_devices: isNewLayout
          ? (formValue?.DW_devices ?? []).map((device: any) => {
            const { DW_description, DW_source, ...deviceRest } = device;
            return {
              ...deviceRest,
              deviceType: this.resolveDeviceTypeValue(device.deviceType),
              userEnabledAnalyticsEngineIds,
            };
          })
          : [],
      };
      this.updateSubscription = this._siteService.addSitesMasterUser(requestPayload).subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res) {
            // Resolve the site id (edit: known; add: from the response) and sync team assignment.
            const resolvedSiteId = this.isEditMode
              ? `${(this.siteData as any)?._id ?? ''}`
              : `${res?.data?._id ?? res?.site?._id ?? res?.data?.site?._id ?? res?._id ?? res?.result?._id ?? ''}`;
            if (resolvedSiteId) {
              this.syncSiteTeams(resolvedSiteId, selectedTeamIds, currentSiteName);
              // Edit only: a site being added has no devices yet to take streams from.
              if (this.isEditMode) {
                this.persistLiveUrls(resolvedSiteId).catch(() => this._utils.showErrorInfo(
                  '', 'Site was saved, but the live urls could not be updated.'
                ));
                this.savePbxSpeakerRecords(resolvedSiteId, requestPayload.speaker_numbers);
              }
              // Only on add: in edit mode the schedule is managed from Site Settings, which
              // is where it has always been.
              if (!this.isEditMode) {
                this.saveVisitorSchedules(resolvedSiteId);
                this.saveAlertScheduleDrafts(resolvedSiteId);
              }
            }
            this._utils.showErrorSuccess(this.siteData && this.siteData._id ? 'Site Updated Successfully' : 'Site Created Successfully');
            this.finishSiteCreation();
          } else {
            this._utils.showErrorInfo('Something wrong!');
          }
        },
        error: (e) => { this.isLoading = false; console.log(e) },
        complete: () => this.updateSubscription.unsubscribe()
      });
    }
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) this.updateSubscription.unsubscribe();
    if (this.siteDetailSubscription) this.siteDetailSubscription.unsubscribe();
    if (this.dwUsersSubscription) this.dwUsersSubscription.unsubscribe();
    if (this.dwDeviceTypesSubscription) this.dwDeviceTypesSubscription.unsubscribe();
    if (this.dwEventsSubscription) this.dwEventsSubscription.unsubscribe();
    if (this.dwActionsSubscription) this.dwActionsSubscription.unsubscribe();
    if (this.dwLayoutsSubscription) this.dwLayoutsSubscription.unsubscribe();
    if (this.dwServersSubscription) this.dwServersSubscription.unsubscribe();
    if (this.teamsSubscription) this.teamsSubscription.unsubscribe();
    if (this.responseDefaultSubscription) this.responseDefaultSubscription.unsubscribe();
  }

  onResponseUseDefaultChange(useDefault: boolean): void {
    this.responseUseDefault = useDefault;
    this.syncSiteResponseSteps();
  }

  onSiteResponseStepsChange(steps: ResponseStep[]): void {
    this.savedSiteResponseSteps = steps;
    this.responseSteps = steps;
  }

  private setResponseProtocol(currentSiteData: any): void {
    const protocol = currentSiteData?.response_protocol;
    this.responseUseDefault = protocol?.use_default !== false;
    this.savedSiteResponseSteps = this.responseUseDefault ? [] : (protocol?.steps ?? []);
    this.syncSiteResponseSteps();
  }

  // The site's order over the default's steps; the default may load before or after the site.
  private syncSiteResponseSteps(): void {
    this.responseSteps = resolveResponseSteps(
      { use_default: false, steps: this.savedSiteResponseSteps.length ? this.savedSiteResponseSteps : this.defaultResponseSteps },
      this.defaultResponseSteps,
    ).steps;
  }
}
